import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode, useMemo} from 'react';

import {makeReactContext} from 'lib/makeReactContext';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import {Header2} from 'ui-library/Headers';

const TableContext = makeReactContext<{scrollContents: boolean}>(
  () => ({scrollContents: false}),
  'TableContext'
);

// TODO BNCH-120993: Move this component to ui-library and update components that use it
export function Table({
  children,
  dataTestAttrs,
  scrollContents = false,
}: {
  children: ReactNode;
  dataTestAttrs?: DataTestAttrs;
  /**
   * If true, the table body will be scrollable, if the contents exceed the height of the table.
   * The header will remain sticky.
   */
  scrollContents?: boolean;
}): JSX.Element {
  const contextValue = useMemo(() => ({scrollContents}), [scrollContents]);

  return (
    <TableContext.Provider value={contextValue}>
      <div
        className={classNames(
          css(styles.table),
          scrollContents ? css(styles.tableScrollContents) : undefined
        )}
        {...getTestProps(dataTestAttrs)}
      >
        {children}
      </div>
    </TableContext.Provider>
  );
}

export function Title({children, className}: {children: ReactNode; className?: string}): JSX.Element {
  return (
    <div className={classNames(css(styles.title), className)}>
      <Header2>{children}</Header2>
    </div>
  );
}

export function Header({children, className}: {children: ReactNode; className?: string}): JSX.Element {
  return <div className={classNames('simpleDivTable-header', css(styles.header), className)}>{children}</div>;
}

export function HeaderCell({children, className}: {children: ReactNode; className?: string}): JSX.Element {
  return (
    <div className={classNames('simpleDivTable-headerCell', css(styles.headerCell), className)}>
      {children}
    </div>
  );
}

export function Body({children, className}: {children: ReactNode; className?: string}): JSX.Element {
  return (
    <TableContext.Consumer>
      {({scrollContents}) => (
        <div
          className={classNames(
            'simpleDivTable-body',
            scrollContents ? css(styles.tableBodyScrollContents) : css(styles.tableBody),
            className
          )}
        >
          {children}
        </div>
      )}
    </TableContext.Consumer>
  );
}

export function Row({
  children,
  isBeingEdited,
  className,
  onClick,
  dataTestAttrs,
}: {
  children: ReactNode;
  isBeingEdited?: boolean;
  className?: string;
  onClick?: () => void;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  return (
    <div
      className={classNames(
        'simpleDivTable-row',
        isBeingEdited ? 'is-beingEdited' : undefined,
        className,
        css(styles.row)
      )}
      onClick={onClick ?? undefined}
      {...getTestProps(dataTestAttrs)}
    >
      {children}
    </div>
  );
}

export function Cell({children, className}: {children: ReactNode; className?: string}): JSX.Element {
  return <div className={classNames('simpleDivTable-cell', css(styles.cell), className)}>{children}</div>;
}

export function EmptyCell({children}: {children: ReactNode}): JSX.Element {
  return <div className={css(styles.emptyCell)}>{children}</div>;
}

export const styles = StyleSheet.create({
  table: {
    width: '100%',
    border: 1,
    borderStyle: 'solid',
    borderColor: GRAY_LIGHT,
    borderRadius: 4,
    backgroundColor: WHITE,
    display: 'flex',
    flexDirection: 'column',
  },
  tableScrollContents: {
    // Clip any content that overflows the container. Combined with overflowY: auto on the body,
    // this will scroll the body if the contents exceed the height of the container.
    overflowY: 'hidden',
  },
  tableBody: {
    flex: '0 0 auto',
  },
  tableBodyScrollContents: {
    // Allow the body to shrink to fit the container. Show a scrollbar if the contents exceed the height of the container.
    flex: '0 1 auto',
    overflowY: 'auto',
  },
  title: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    padding: '12px 12px 8px 12px',
    flex: '0 0 auto',
  },
  header: {
    flex: '0 0 auto',
  },
  headerCell: {
    paddingRight: '14px',
    ':last-child': {
      paddingRight: 0,
    },
    display: 'flex',
  },
  cell: {
    paddingRight: '14px',
    wordWrap: 'break-word',
    ':last-child': {
      paddingRight: 0,
    },
    lineHeight: '28px',
  },
  row: {
    cursor: 'pointer',
  },
  emptyCell: {
    width: '100%',
    lineHeight: '28px',
  },
});
